import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Check, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "../../ui/Button";

/* ------------ فرمت عدد با ارقام locale ------------ */
function formatPrice(num, lang) {
  if (lang === "en") return num.toLocaleString("en-US");
  const FA = "۰۱۲۳۴۵۶۷۸۹";
  const AR = "٠١٢٣٤٥٦٧٨٩";
  const digits = lang === "ar" ? AR : FA;
  return num.toString().replace(/\d/g, (d) => digits[+d]);
}

export default function PlanCard({ plan, yearly, index = 0 }) {
  const { t, i18n } = useTranslation("landing");
  const lang = (i18n.language || "fa").split("-")[0];

  const {
    key,
    highlighted,
    priceMode,
    price,
    priceMonthly,
    priceYearly,
    priceOldMonthly,
    featureKeys,
    ctaVariant,
    ctaHref,
  } = plan;

  const displayPrice =
    priceMode === "dynamic"
      ? yearly
        ? priceYearly
        : priceMonthly
      : null;

  const oldPrice = yearly ? priceOldMonthly : null;
  const unit = highlighted ? t("pricing.perMonthK") : t("pricing.perMonth");

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        delay: index * 0.1,
        duration: 0.7,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={clsx(
        "relative flex flex-col rounded-card border-[1.5px] p-7 transition-all duration-500 sm:p-9",
        highlighted
          ? "border-teal-2 bg-gradient-to-b from-surface to-surface-2 shadow-card-lg lg:scale-[1.04]"
          : "border-line bg-surface hover:-translate-y-1.5 hover:border-teal-3 hover:shadow-card-lg"
      )}
    >
      {/* Badge — فقط featured */}
      {highlighted && (
        <span className="absolute -top-3.5 end-1/2 flex translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-br from-gold to-gold-2 px-4 py-2 text-[11.5px] font-extrabold text-amber-950 shadow-[0_10px_26px_rgba(201,162,39,0.35)] rtl:-translate-x-1/2">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
          {t("pricing.badge")}
        </span>
      )}

      {/* Head */}
      <div className={clsx("mb-6", highlighted && "pt-2")}>
        <h3 className="mb-1.5 text-[19px] font-black tracking-tight text-ink sm:text-[20px]">
          {t(`${key}.title`)}
        </h3>
        <p className="text-[13px] text-ink-soft sm:text-[13.5px]">
          {t(`${key}.desc`)}
        </p>
      </div>

      {/* Price */}
      <div className="relative mb-7 min-h-[68px]">
        {priceMode === "contact" ? (
          <b className="text-[36px] font-black leading-none tracking-tight text-ink sm:text-[40px]">
            {t("pricing.contact")}
          </b>
        ) : priceMode === "static" ? (
          <>
            <b className="text-[40px] font-black leading-none tracking-[-2px] text-ink sm:text-[48px]">
              {formatPrice(0, lang)}
            </b>
            <span className="ms-2 text-[13.5px] text-ink-soft">{unit}</span>
          </>
        ) : (
          <>
            {oldPrice && (
              <span className="absolute -top-3.5 start-0 text-[14px] text-ink-mute line-through">
                {formatPrice(oldPrice, lang)}
              </span>
            )}
            <b className="text-[40px] font-black leading-none tracking-[-2px] text-ink transition-all duration-300 sm:text-[48px]">
              {formatPrice(displayPrice, lang)}
            </b>
            <span className="ms-2 text-[13.5px] text-ink-soft">{unit}</span>
          </>
        )}
      </div>

      {/* Features */}
      <ul className="mb-8 flex flex-1 flex-col gap-3.5">
        {featureKeys.map((fk, i) => (
          <li
            key={fk}
            className={clsx(
              "flex items-start gap-3 text-[14px] font-medium text-ink",
              fk === "f5" && highlighted && "text-amber-700 dark:text-gold-2"
            )}
          >
            <span
              className={clsx(
                "mt-0.5 grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full",
                fk === "f5" && highlighted
                  ? "bg-gold-soft text-amber-700 dark:bg-gold-2/15 dark:text-gold-2"
                  : "bg-teal-soft text-teal dark:bg-teal-2/15 dark:text-teal-3"
              )}
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            {t(`${key}.${fk}`)}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Button
        as={Link}
        to={ctaHref}
        variant={ctaVariant}
        size="lg"
        className="w-full"
      >
        {t(`${key}.cta`)}
        {highlighted && " ✨"}
      </Button>
    </motion.div>
  );
}